import type { Group } from '@reliance/types/models/MobileDevice';
import type { Facility } from '@reliance/types/models/Facility';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import api from '../common/api';
import ConfirmModal from '../common/dialogs/ConfirmModal';
import type { IStatusResponse } from '../common/reliance-api-service';
import { useMobileDeviceGroups } from '../hooks/MobileDeviceGroup';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import AddEditModal from '../mobile-device-group/AddEditModal';
import BaseHtmlTable from '../table/BaseHtmlTable';
import { useAlerts } from '../alert/AlertContext';

type FacilityDeviceGroupsTabProps = {
  facility: Facility;
};

const FacilityDeviceGroupsTab: React.FC<FacilityDeviceGroupsTabProps> = ({ facility }) => {
  const alerts = useAlerts();
  const facilityId = facility.id as number;
  const filters = useMemo(() => ({ facilityId, page: 1, perPage: 500 }), [facilityId]);
  const { result, loading, execute } = useMobileDeviceGroups(filters);
  const rows = result?.data ?? [];

  useNotifyTabLoading('device-groups', loading);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);

  const openAdd = () => {
    setEditingGroup(null);
    setModalOpen(true);
  };

  const openEdit = (g: Group) => {
    setEditingGroup(g);
    setModalOpen(true);
  };

  const onModalClose = useCallback(
    (saved: boolean) => {
      setModalOpen(false);
      setEditingGroup(null);
      if (saved) {
        void execute();
      }
    },
    [execute]
  );

  const columnHelper = createColumnHelper<Group>();
  const columnDefs = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Name',
      }),
      columnHelper.display({
        id: 'options',
        header: 'Options',
        meta: { cellClassName: 'text-nowrap', cellStyle: { maxWidth: 200 } },
        cell: (ctx) => (
          <>
            <Button
              size="sm"
              variant="secondary"
              className="me-1"
              onClick={() => openEdit(ctx.row.original)}>
              Edit
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setGroupToDelete(ctx.row.original)}>
              Remove
            </Button>
          </>
        ),
      }),
    ],
    []
  );

  const handleDelete = async () => {
    if (groupToDelete == null) {
      return;
    }
    try {
      const res = await api.delete<IStatusResponse>(`/mobile-device/group/${groupToDelete.id}`);
      if (!res.data?.success) {
        alerts.error('An unexpected error occurred', true);
        return;
      }
      void execute();
    } catch {
      alerts.error('An unexpected error occurred', true);
    }
  };

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div className="content">
        <Button className="btn-labeled btn-primary mb-2" onClick={openAdd}>
          <span className="btn-label">
            <i className="fas fa-plus-circle" />
          </span>
          Add Group
        </Button>
        <BaseHtmlTable rows={rows} columnDefs={columnDefs} paginationMode="client" perPage={100} loading={loading} />
        <AddEditModal
          show={modalOpen}
          onClose={onModalClose}
          facilityId={facilityId}
          group={editingGroup}
        />
        <ConfirmModal
          title="Delete Group"
          show={groupToDelete != null}
          acceptText="Delete"
          rejectText="Cancel"
          onClose={() => setGroupToDelete(null)}
          onAccept={handleDelete}>
          {groupToDelete != null && <p>Are you sure you want to delete {groupToDelete.name}?</p>}
        </ConfirmModal>
      </div>
    </div>
  );
};

export default FacilityDeviceGroupsTab;
